import { KendoOoxml } from '@progress/kendo-react-excel-export';
import { DlpConfigurationModel } from '../services/DlpConfiguration/DlpConfigurationService';

// Preserve nested label structure as JSON; simple lists remain semicolon-separated.
function toCellValue(value: unknown): string | number | boolean {
    if (value === null || value === undefined) return '';
    if (Array.isArray(value)) {
        return value.some(v => typeof v === 'object' && v !== null) ? JSON.stringify(value) : value.join(';');
    }
    return value as string | number | boolean;
}

function toSheet<T extends object>(name: string, rows: T[], columns: (keyof T)[]): KendoOoxml.WorkbookSheet {
    return {
        name,
        rows: [
            { cells: columns.map(c => ({ value: String(c), bold: true })) },
            ...rows.map(r => ({ cells: columns.map(c => ({ value: toCellValue(r[c]) })) }))
        ]
    };
}

// One sheet per table plus an import-friendly dlp_labels vocabulary sheet:
// dlp_scopes has one row per brand (brand_id empty = all brands).
export function buildDlpConfigurationWorkbook(configuration: DlpConfigurationModel, projectId?: string): KendoOoxml.WorkbookOptions {
    let { projects, scopes, scopeBrands, profiles } = configuration;
    // The page always passes its resolved project ID. The unscoped utility remains
    // available for explicit bulk workbook/import tooling, not page navigation.
    if (projectId !== undefined) {
        projects = projects.filter(project => project.id === projectId);
        if (projects.length !== 1) throw new Error('Export requires exactly one selected DLP Databricks project.');
        const foreignScopeIds = new Set(scopes.filter(scope => scope.project_id !== projectId).map(scope => scope.scope_id));
        scopes = scopes.filter(scope => scope.project_id === projectId);
        if (scopes.some(scope => foreignScopeIds.has(scope.scope_id))) throw new Error('Ambiguous scope IDs cannot be exported.');
        const scopeIds = new Set(scopes.map(scope => scope.scope_id));
        scopeBrands = scopeBrands.filter(link => scopeIds.has(link.scope_id));
        profiles = profiles.filter(profile => profile.project_id === projectId);
    }
    const scopeRows = scopes.flatMap(scope => {
        const brandIds = scope.all_brands ? [null] : scopeBrands.filter(sb => sb.scope_id === scope.scope_id).map(sb => sb.brand_id);
        return brandIds.map(brand_id => ({ ...scope, brand_id }));
    });

    const labelRows = projects.flatMap(project => project.dlp_labels.map(label => ({
        project_id: project.id, label_id: label.id, label_name: label.name, parent_label_id: label.parent_id
    })));

    return {
        sheets: [
            toSheet('dlp_projects', projects, ['id', 'dlp_project_code', 'source_family', 'channels', 'group_by', 'label_field', 'ranking_metric', 'window_days', 'exclude_owned_media', 'exclude_already_sent', 'empty_label_fallback', 'is_active', 'dlp_labels']),
            toSheet('dlp_scopes', scopeRows, ['project_id', 'name', 'brand_id', 'channels', 'countries', 'industries', 'quota_profile', 'start_from', 'ended_at']),
            toSheet('dlp_quota_profiles', profiles, ['profile_name', 'project_id', 'channel', 'label', 'max_docs']),
            toSheet('dlp_labels', labelRows, ['project_id', 'label_id', 'label_name', 'parent_label_id'])
        ]
    };
}
